import {Component, Input, OnInit, OnChanges, SimpleChanges, AfterViewInit, ChangeDetectorRef} from '@angular/core';
import feather from 'feather-icons';
import {ProductsService} from '../../../../services/products/products.service';
import {forkJoin, of} from 'rxjs';
import {map, catchError} from 'rxjs/operators';

@Component({
  selector: 'app-detail-document-product-table',
  templateUrl: './detail-document-product-table.component.html',
  styleUrls: ['./detail-document-product-table.component.scss']
})
export class DetailDocumentProductTableComponent implements OnInit, OnChanges, AfterViewInit {

  @Input() firstPriceLabel = "PRIX D'ACHAT";
  @Input() secondPriceLabel = 'PRIX DE VENTE';
  /** Regroupement réf. / couleur fermé : 2e colonne (prix de vente) → « — » au lieu d’une somme. */
  @Input() refGroupCollapsedSecondPriceIsDash = false;
  @Input() sourceLignes: any[] | null = null;

  isLoading = false;
  lignesDocument: any[] = [];
  private productsById: { [key: number]: any } = {};
  expandedBarcodeGroups = new Set<string>();
  expandedCouleurGroups = new Set<string>();
  readonly detailTableColspan = 6;

  constructor(
    private productsService: ProductsService,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    if (this.sourceLignes?.length) {
      this.reloadLignes();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['sourceLignes'] && !changes['sourceLignes'].firstChange) {
      this.reloadLignes();
    }
  }

  ngAfterViewInit(): void {
    feather.replace();
  }

  private reloadLignes(): void {
    this.expandedBarcodeGroups = new Set();
    this.expandedCouleurGroups = new Set();
    const raw = (this.sourceLignes || []).filter((l) => this.isProductSourceLigne(l));
    if (!raw.length) {
      this.lignesDocument = [];
      this.isLoading = false;
      this.cdr.detectChanges();
      return;
    }
    this.isLoading = true;
    const normalizedRaw = raw.map((l) => this.normalizeLigneForDetailDisplay(l));
    this.loadLignesWithProductValeurUnitaire(normalizedRaw).subscribe({
      next: (lignesDoc) => {
        this.lignesDocument = this.sortLignesForProductGrouping(lignesDoc);
        this.isLoading = false;
        this.cdr.detectChanges();
        setTimeout(() => feather.replace(), 0);
      },
      error: () => {
        this.lignesDocument = this.sortLignesForProductGrouping(
          this.buildLignesDocument(normalizedRaw, {})
        );
        this.isLoading = false;
        this.cdr.detectChanges();
        setTimeout(() => feather.replace(), 0);
      }
    });
  }

  private isProductSourceLigne(ligne: any): boolean {
    if (!ligne) {
      return false;
    }
    const typeRaw = String(ligne.type_ligne ?? ligne.type ?? '').toLowerCase().trim();
    if (typeRaw && ['rubrique', 'separator', 'separateur', 'texte', 'text', 'subtotal', 'sous_total'].includes(typeRaw)) {
      return false;
    }
    return (
      ligne.product_id != null ||
      ligne.code_barre != null ||
      ligne.code_bar != null ||
      !!String(ligne.libelle || ligne.designation || '').trim()
    );
  }
  /** Libellés colonnes prix (alignés popup detail-cmd-fourniss / capture). */
  getDetailFirstPriceLabel(): string {
    return this.firstPriceLabel;
  }

  getDetailSecondPriceLabel(): string {
    return this.secondPriceLabel;
  }

  /**
   * Normalise une ligne API (ref fabricant, code-barre, couleur, taille) — aligné document / approve-vente.
   */
  private normalizeLigneForDetailDisplay(ligne: any): any {
    const embedded =
      ligne?.produit ??
      ligne?.product ??
      ligne?.product_data ??
      ligne?.produit_data ??
      null;
    const src =
      embedded && typeof embedded === 'object' ? { ...embedded, ...ligne } : ligne;

    const refInterne =
      src.ref_interne ??
      (src.reference && src.reference !== '-' && src.reference !== '--' ? src.reference : null) ??
      src.sku ??
      src.code_article ??
      null;
    const codeBarRaw = src.code_bar ?? src.code_barre ?? null;
    const codeBarTrim = codeBarRaw != null ? String(codeBarRaw).trim() : '';
    const codeBar = codeBarTrim === '' || codeBarTrim === '0' ? null : codeBarTrim;

    const couleur =
      src.couleur ??
      src.couleur_libelle ??
      src.libelle_couleur ??
      src.nom_couleur ??
      null;
    const taille =
      src.taille ??
      src.taille_libelle ??
      src.libelle_taille ??
      src.nom_taille ??
      null;

    let refFabRaw = src.ref_fabricant ?? src.ref_fabriquant ?? null;
    let refFabTrim = refFabRaw != null ? String(refFabRaw).trim() : '';
    if (refFabTrim === '' || refFabTrim === '-' || refFabTrim === '—') {
      const libRaw = String(src.libelle ?? src.designation ?? '').trim();
      const libParts = libRaw.match(/^(.+?)\s*\/\s*(\S+)\s*$/);
      if (libParts) {
        refFabTrim = libParts[2].trim();
      }
    }
    const refFabricant =
      refFabTrim === '' || refFabTrim === '-' || refFabTrim === '—' ? null : refFabTrim;

    return {
      ...ligne,
      ref_interne: refInterne,
      reference: refInterne,
      ref_fabricant: refFabricant,
      ref_fabriquant: refFabricant,
      code_bar: codeBar,
      code_barre: codeBar,
      couleur: couleur != null && String(couleur).trim() !== '' ? String(couleur).trim() : null,
      taille: taille != null && String(taille).trim() !== '' ? String(taille).trim() : null,
    };
  }

  /** Prix catalogue ERP (products.achat_ht / vente_ht), pas la ligne document seule. */
  private readProductCatalogAchatHt(product: any): number | null {
    const p = product?.data ?? product;
    if (!p || typeof p !== 'object') {
      return null;
    }
    const raw = p.achat_ht ?? p.prix_achat;
    if (raw === undefined || raw === null || raw === '') {
      return null;
    }
    const n = typeof raw === 'number' ? raw : parseFloat(String(raw).replace(',', '.'));
    return Number.isFinite(n) ? n : null;
  }

  private readProductCatalogVenteHt(product: any): number | null {
    const p = product?.data ?? product;
    if (!p || typeof p !== 'object') {
      return null;
    }
    const raw = p.vente_ht ?? p.prix_vente;
    if (raw === undefined || raw === null || raw === '') {
      return null;
    }
    const n = typeof raw === 'number' ? raw : parseFloat(String(raw).replace(',', '.'));
    return Number.isFinite(n) ? n : null;
  }

  private mergeProductIntoNormalizedLigne(ligne: any, product: any): any {
    const p = product?.data ?? product;
    if (!p || typeof p !== 'object') {
      return ligne;
    }
    const nested = p.produit ?? p.product ?? null;
    const refFab =
      p.ref_fabricant ??
      p.ref_fabriquant ??
      nested?.ref_fabricant ??
      nested?.ref_fabriquant ??
      null;
    return this.normalizeLigneForDetailDisplay({
      ...ligne,
      ref_fabricant: ligne.ref_fabricant || ligne.ref_fabriquant || refFab || null,
      ref_fabriquant: ligne.ref_fabriquant || ligne.ref_fabricant || refFab || null,
      code_bar: ligne.code_bar || p.code_barre || p.code_bar || null,
      code_barre: ligne.code_barre || p.code_barre || p.code_bar || null,
      couleur:
        ligne.couleur ||
        p.couleur ||
        p.couleur_libelle ||
        p.libelle_couleur ||
        null,
      taille:
        ligne.taille ||
        p.taille ||
        p.taille_libelle ||
        p.libelle_taille ||
        null,
    });
  }

  private lineGroupSortKey(ligne: any): string {
    const pid = ligne.product_id != null ? Number(ligne.product_id) : NaN;
    if (Number.isFinite(pid)) {
      const fromCatalog = this.productsById[pid];
      const ref = this.normalizeRefFabricant(
        ligne.ref_fabricant ??
          fromCatalog?.ref_fabricant ??
          fromCatalog?.ref_fabriquant
      );
      if (ref) {
        return `ref:${ref.toLowerCase()}`;
      }
    }
    const refLine = this.normalizeRefFabricant(ligne.ref_fabricant);
    if (refLine) {
      return `ref:${refLine.toLowerCase()}`;
    }
    const des = String(ligne.libelle || '').trim().toLowerCase();
    if (des) {
      return `des:${des}`;
    }
    return `solo:${String(ligne.code_barre || '')}`;
  }

  private sortLignesForProductGrouping(lignes: any[]): any[] {
    return [...(lignes || [])].sort((a, b) => {
      const g = this.lineGroupSortKey(a).localeCompare(this.lineGroupSortKey(b), 'fr', {
        sensitivity: 'base',
      });
      if (g !== 0) {
        return g;
      }
      const ca = String(a.couleur ?? '—')
        .trim()
        .toLowerCase();
      const cb = String(b.couleur ?? '—')
        .trim()
        .toLowerCase();
      const c = ca.localeCompare(cb, 'fr', { sensitivity: 'base' });
      if (c !== 0) {
        return c;
      }
      return String(a.code_barre ?? '').localeCompare(String(b.code_barre ?? ''), 'fr', {
        numeric: true,
      });
    });
  }

  private loadLignesWithProductValeurUnitaire(lignes: any[]) {
    const productIds = [...new Set(
      lignes.map((l: any) => l.product_id ?? l.product?.id).filter((id: any) => id != null)
    )] as number[];
    if (productIds.length === 0) {
      this.productsById = {};
      return of(this.buildLignesDocument(lignes, {}));
    }
    const requests: { [key: number]: ReturnType<ProductsService['getDetailsProduct']> } = {};
    productIds.forEach(id => {
      requests[id] = this.productsService.getDetailsProduct(id).pipe(
        map((res: any) => res?.data ?? res),
        catchError(() => of(null))
      );
    });
    return forkJoin(requests).pipe(
      map((productsMap: { [key: number]: any }) => {
        this.productsById = productsMap || {};
        const merged = lignes.map((ligne: any) => {
          const pid = ligne.product_id ?? ligne.product?.id;
          const product = pid != null ? productsMap[pid] : null;
          return product
            ? this.mergeProductIntoNormalizedLigne(ligne, product)
            : this.normalizeLigneForDetailDisplay(ligne);
        });
        return this.buildLignesDocument(merged, productsMap);
      })
    );
  }

  private buildLignesDocument(lignes: any[], productsMap: { [key: number]: any }): any[] {
    return lignes.map((ligne: any) => {
      const normalized = this.normalizeLigneForDetailDisplay(ligne);
      const pid = normalized.product_id ?? normalized.product?.id;
      const product = pid != null ? productsMap[pid] : null;
      const catalogAchatHt = this.readProductCatalogAchatHt(product);
      const catalogVenteHt = this.readProductCatalogVenteHt(product);
      const prixAchatRaw =
        catalogAchatHt ??
        normalized.prix_achat ??
        normalized.valeur_unitaire ??
        product?.valeur_unitaire ??
        0;
      const prixAchat =
        typeof prixAchatRaw === 'number' ? prixAchatRaw : parseFloat(String(prixAchatRaw)) || 0;
      const prixVenteRaw =
        catalogVenteHt ??
        normalized.prix_vente ??
        normalized.prix_ht ??
        0;
      const prixVente =
        typeof prixVenteRaw === 'number' ? prixVenteRaw : parseFloat(String(prixVenteRaw)) || 0;
      const { couleur, taille } = this.extractCouleurTailleFromProductLike(normalized);
      const fromProduct = product ? this.extractCouleurTailleFromProductLike(product) : { couleur: '', taille: '' };
      const refFab = this.normalizeRefFabricant(
        normalized.ref_fabricant ??
          normalized.ref_fabriquant ??
          product?.ref_fabricant ??
          product?.ref_fabriquant
      );
      const codeBarre = String(
        normalized.code_barre ??
          normalized.code_bar ??
          product?.code_barre ??
          product?.code_bar ??
          ''
      ).trim();
      const libelleRaw =
        normalized.libelle ||
        normalized.designation ||
        product?.nom ||
        product?.designation ||
        'Sans dÃ©signation';
      return {
        libelle: String(libelleRaw).trim(),
        qnt: parseFloat(normalized.qnt || '0'),
        remise: normalized.remise || '0',
        achat_ht: prixAchat,
        vente_ht: prixVente,
        prix_achat: prixAchat,
        prix_ht: prixVente,
        prix_unitaire:
          parseFloat(String(normalized.valeur_unitaire ?? normalized.prix_ht ?? prixVente ?? 0)) || 0,
        prix_vente: prixVente,
        total_ht:
          normalized.total_ht != null
            ? parseFloat(String(normalized.total_ht))
            : undefined,
        product_id: pid != null ? Number(pid) : null,
        ref_fabricant: refFab,
        ref_interne: normalized.ref_interne ?? normalized.reference ?? null,
        couleur: couleur || fromProduct.couleur,
        taille: taille || fromProduct.taille,
        code_barre: codeBarre,
      };
    });
  }

  formatPrice(value: string | number): string {
    const num = typeof value === 'string' ? parseFloat(value) || 0 : value;
    return num.toLocaleString('fr-FR', {
      minimumFractionDigits: 3,
      maximumFractionDigits: 3
    });
  }

  formatBarcodeDisplay(code: any): string {
    const raw = code != null ? String(code).replace(/\s/g, '').trim() : '';
    if (!raw) {
      return '—';
    }
    if (/^\d{13}$/.test(raw)) {
      return `${raw[0]} ${raw.slice(1, 7)} ${raw.slice(7)}`;
    }
    if (/^\d{12}$/.test(raw)) {
      return `${raw.slice(0, 6)} ${raw.slice(6)}`;
    }
    if (/^\d{8}$/.test(raw)) {
      return `${raw.slice(0, 4)} ${raw.slice(4)}`;
    }
    return String(code).trim();
  }

  formatCouleurTailleLine(index: number): string {
    const ligne = this.lignesDocument[index];
    if (!ligne) {
      return '';
    }
    const c = String(ligne.couleur ?? '').trim();
    const t = String(ligne.taille ?? '').trim();
    if (c && t) {
      return `${c} / ${t}`;
    }
    return c || t || '';
  }

  private normalizeRefFabricant(raw: any): string {
    if (raw == null) {
      return '';
    }
    return String(raw).trim();
  }

  private extractCouleurTailleFromProductLike(p: any): { couleur: string; taille: string } {
    if (!p || typeof p !== 'object') {
      return { couleur: '', taille: '' };
    }
    const couleurRaw =
      p.couleur ??
      p.couleur_libelle ??
      p.libelle_couleur ??
      p.nom_couleur ??
      p.color ??
      p.couleur_nom;
    const tailleRaw =
      p.taille ??
      p.taille_libelle ??
      p.libelle_taille ??
      p.nom_taille ??
      p.size ??
      p.taille_nom;
    const couleur =
      couleurRaw != null && String(couleurRaw).trim() !== '' ? String(couleurRaw).trim() : '';
    const taille =
      tailleRaw != null && String(tailleRaw).trim() !== '' ? String(tailleRaw).trim() : '';
    return { couleur, taille };
  }

  private getLineAt(index: number): any | null {
    return this.lignesDocument[index] ?? null;
  }

  getProductLineGroupKey(index: number): string {
    const ligne = this.getLineAt(index);
    if (!ligne) {
      return `solo:${index}`;
    }
    const pid = ligne.product_id != null ? Number(ligne.product_id) : NaN;
    if (Number.isFinite(pid)) {
      const fromCatalog = this.productsById[pid];
      const ref = this.normalizeRefFabricant(
        ligne.ref_fabricant ??
          ligne.ref_fabriquant ??
          fromCatalog?.ref_fabricant ??
          fromCatalog?.ref_fabriquant
      );
      if (ref) {
        return `ref:${ref}`;
      }
    }
    const refLine = this.normalizeRefFabricant(ligne.ref_fabricant ?? ligne.ref_fabriquant);
    if (refLine) {
      return `ref:${refLine}`;
    }
    const des = String(ligne.libelle || '')
      .trim()
      .toLowerCase();
    if (des) {
      return `des:${des}`;
    }
    return `solo:${index}`;
  }

  getProductLineGroupMemberIndices(groupKey: string): number[] {
    const out: number[] = [];
    (this.lignesDocument || []).forEach((_, i) => {
      if (this.getProductLineGroupKey(i) === groupKey) {
        out.push(i);
      }
    });
    return out;
  }

  getProductLineCouleurNorm(index: number): string {
    const ligne = this.getLineAt(index);
    if (!ligne) {
      return '—';
    }
    const fromItem = String(ligne.couleur ?? '').trim();
    if (fromItem) {
      return fromItem;
    }
    const pid = ligne.product_id != null ? Number(ligne.product_id) : NaN;
    if (Number.isFinite(pid)) {
      const fromCatalog = this.productsById[pid];
      const { couleur } = this.extractCouleurTailleFromProductLike(fromCatalog);
      if (couleur) {
        return couleur;
      }
    }
    return '—';
  }

  getProductLineCouleurGroupKey(index: number): string {
    const desKey = this.getProductLineGroupKey(index);
    const couleur = this.getProductLineCouleurNorm(index);
    return `${desKey}|c:${couleur.toLowerCase()}`;
  }

  getProductLineCouleurMemberIndices(index: number): number[] {
    const desKey = this.getProductLineGroupKey(index);
    const couleurNorm = this.getProductLineCouleurNorm(index).toLowerCase();
    return this.getProductLineGroupMemberIndices(desKey).filter(
      (mi) => this.getProductLineCouleurNorm(mi).toLowerCase() === couleurNorm
    );
  }

  isProductLineGroupHead(index: number): boolean {
    const key = this.getProductLineGroupKey(index);
    const members = this.getProductLineGroupMemberIndices(key);
    return members.length > 0 && members[0] === index;
  }

  isProductLineCouleurHead(index: number): boolean {
    const colorMembers = this.getProductLineCouleurMemberIndices(index);
    return colorMembers.length > 0 && colorMembers[0] === index;
  }

  productLineGroupCanToggle(index: number): boolean {
    const ligne = this.getLineAt(index);
    if (!ligne) {
      return false;
    }
    const refLabel = this.getProductLineRefFabricantLabel(index);
    const hasGroupLabel = !!String(ligne.libelle || '').trim() || refLabel !== '—';
    if (!hasGroupLabel) {
      return false;
    }
    return this.getProductLineGroupMemberIndices(this.getProductLineGroupKey(index)).length > 1;
  }

  productLineCouleurGroupCanToggle(index: number): boolean {
    if (!this.isProductLineBarcodeGroupExpanded(index)) {
      return false;
    }
    return this.getProductLineCouleurMemberIndices(index).length > 1;
  }

  isProductLineBarcodeGroupExpanded(index: number): boolean {
    return this.expandedBarcodeGroups.has(this.getProductLineGroupKey(index));
  }

  isProductLineCouleurGroupExpanded(index: number): boolean {
    return this.expandedCouleurGroups.has(this.getProductLineCouleurGroupKey(index));
  }

  getProductLineGroupBarcodeCount(index: number): number {
    return this.getProductLineGroupMemberIndices(this.getProductLineGroupKey(index)).length;
  }

  getProductLineGroupCouleurCount(index: number): number {
    const members = this.getProductLineGroupMemberIndices(this.getProductLineGroupKey(index));
    const couleurs = new Set<string>();
    for (const mi of members) {
      couleurs.add(this.getProductLineCouleurNorm(mi).toLowerCase());
    }
    return couleurs.size;
  }

  getProductLineCouleurBarcodeCount(index: number): number {
    return this.getProductLineCouleurMemberIndices(index).length;
  }

  shouldShowProductLineRefOpenSummary(index: number): boolean {
    return (
      this.productLineGroupCanToggle(index) &&
      this.isProductLineGroupHead(index) &&
      this.isProductLineBarcodeGroupExpanded(index)
    );
  }

  getProductLineRefOpenBannerTrailingColspan(): number {
    return this.detailTableColspan - 2;
  }

  shouldShowProductLineSplitLayout(index: number): boolean {
    return this.shouldShowProductLineFullEditor(index);
  }

  shouldShowProductLineCouleurBannerRow(index: number): boolean {
    if (!this.shouldShowProductLineSplitLayout(index)) {
      return false;
    }
    if (this.getProductLineCouleurNorm(index) === '—') {
      return false;
    }
    return this.isProductLineCouleurHead(index);
  }

  isProductLineVariantProductRow(index: number): boolean {
    return (
      this.shouldShowProductLineFullEditor(index) &&
      this.productLineGroupCanToggle(index) &&
      this.isProductLineBarcodeGroupExpanded(index)
    );
  }

  getProductLineRefFabricantLabel(index: number): string {
    const ligne = this.getLineAt(index);
    if (!ligne) {
      return '—';
    }
    const pid = ligne.product_id != null ? Number(ligne.product_id) : NaN;
    let ref = '';
    if (Number.isFinite(pid)) {
      const fromCatalog = this.productsById[pid];
      ref = this.normalizeRefFabricant(
        ligne.ref_fabricant ??
          ligne.ref_fabriquant ??
          fromCatalog?.ref_fabricant ??
          fromCatalog?.ref_fabriquant
      );
    } else {
      ref = this.normalizeRefFabricant(ligne.ref_fabricant ?? ligne.ref_fabriquant);
    }
    if (ref) {
      return ref;
    }
    const des = String(ligne.libelle || '').trim();
    return des || '—';
  }

  isProductLineRefGroupCollapsedHead(index: number): boolean {
    return (
      this.productLineGroupCanToggle(index) &&
      this.isProductLineGroupHead(index) &&
      !this.isProductLineBarcodeGroupExpanded(index)
    );
  }

  isProductLineCouleurGroupCollapsedHead(index: number): boolean {
    return (
      this.isProductLineBarcodeGroupExpanded(index) &&
      this.isProductLineCouleurHead(index) &&
      this.productLineCouleurGroupCanToggle(index) &&
      !this.isProductLineCouleurGroupExpanded(index)
    );
  }

  isProductLineInCollapsedGroup(index: number): boolean {
    const ligne = this.getLineAt(index);
    if (!ligne) {
      return false;
    }
    const key = this.getProductLineGroupKey(index);
    const members = this.getProductLineGroupMemberIndices(key);
    if (members.length <= 1) {
      return false;
    }
    if (!this.expandedBarcodeGroups.has(key)) {
      return true;
    }
    const colorMembers = this.getProductLineCouleurMemberIndices(index);
    if (colorMembers.length <= 1) {
      return false;
    }
    const couleurKey = this.getProductLineCouleurGroupKey(index);
    return !this.expandedCouleurGroups.has(couleurKey);
  }

  shouldShowProductLineFullEditor(index: number): boolean {
    if (!this.productLineGroupCanToggle(index)) {
      return true;
    }
    return !this.isProductLineInCollapsedGroup(index);
  }

  shouldShowProductLineDesignationLeaf(index: number): boolean {
    if (!this.shouldShowProductLineFullEditor(index)) {
      return false;
    }
    if (this.shouldShowProductLineCouleurBannerRow(index)) {
      return this.shouldShowProductLineBarcodeCell(index);
    }
    return true;
  }

  shouldShowProductLineBarcodeColumnCouleurHint(index: number): boolean {
    return (
      this.isProductLineBarcodeGroupExpanded(index) &&
      this.isProductLineCouleurHead(index) &&
      this.productLineCouleurGroupCanToggle(index) &&
      !this.isProductLineRefGroupCollapsedHead(index)
    );
  }

  shouldShowProductLineBarcodeCell(index: number): boolean {
    if (!this.productLineGroupCanToggle(index)) {
      return true;
    }
    if (!this.isProductLineBarcodeGroupExpanded(index)) {
      return !this.isProductLineGroupHead(index);
    }
    if (!this.productLineCouleurGroupCanToggle(index)) {
      return true;
    }
    if (!this.isProductLineCouleurGroupExpanded(index)) {
      return !this.isProductLineCouleurHead(index);
    }
    return true;
  }

  isProductLineVisible(index: number): boolean {
    const ligne = this.getLineAt(index);
    if (!ligne) {
      return true;
    }
    const key = this.getProductLineGroupKey(index);
    const members = this.getProductLineGroupMemberIndices(key);
    if (members.length <= 1) {
      return true;
    }
    if (!this.expandedBarcodeGroups.has(key)) {
      return members[0] === index;
    }
    const colorMembers = this.getProductLineCouleurMemberIndices(index);
    if (colorMembers.length <= 1) {
      return true;
    }
    const couleurKey = this.getProductLineCouleurGroupKey(index);
    if (!this.expandedCouleurGroups.has(couleurKey)) {
      return colorMembers[0] === index;
    }
    return true;
  }

  private getLineAchatUnitPrice(ligne: any): number {
    const raw =
      ligne?.achat_ht ??
      ligne?.prix_achat ??
      ligne?.valeur_unitaire ??
      ligne?.prix_unitaire ??
      0;
    const n = typeof raw === 'number' ? raw : parseFloat(String(raw));
    return Number.isFinite(n) ? n : 0;
  }

  private getLineVenteUnitPrice(ligne: any): number {
    const raw = ligne?.vente_ht ?? ligne?.prix_vente ?? ligne?.prix_ht ?? 0;
    const n = typeof raw === 'number' ? raw : parseFloat(String(raw));
    return Number.isFinite(n) ? n : 0;
  }

  private getLineUnitPrice(ligne: any): number {
    return this.getLineAchatUnitPrice(ligne) || this.getLineVenteUnitPrice(ligne);
  }

  private getLineTotalHT(ligne: any): number {
    const fromApi = ligne?.total_ht;
    if (fromApi != null && fromApi !== '') {
      const t = parseFloat(String(fromApi));
      if (Number.isFinite(t)) {
        return Number(t.toFixed(3));
      }
    }
    const q = Number(ligne.qnt) || 0;
    const prix = this.getLineUnitPrice(ligne);
    const remise = parseFloat(String(ligne.remise || '0')) / 100;
    return Number((q * prix * (1 - remise)).toFixed(3));
  }

  private sumProductLineQuantities(indices: number[]): number {
    let sum = 0;
    for (const i of indices || []) {
      const it = this.lignesDocument[i];
      if (it) {
        const q = Number(it.qnt);
        if (Number.isFinite(q)) {
          sum += q;
        }
      }
    }
    return sum;
  }

  private sumProductLineHtNet(indices: number[]): number {
    let sum = 0;
    for (const i of indices || []) {
      const it = this.lignesDocument[i];
      if (it && String(it.libelle || '').trim()) {
        sum += this.getLineTotalHT(it);
      }
    }
    return Number(sum.toFixed(3));
  }

  getProductLineQuantityDisplay(index: number): number {
    const ligne = this.getLineAt(index);
    if (!ligne) {
      return 0;
    }
    if (this.isProductLineRefGroupCollapsedHead(index)) {
      return this.sumProductLineQuantities(
        this.getProductLineGroupMemberIndices(this.getProductLineGroupKey(index))
      );
    }
    if (!this.productLineGroupCanToggle(index)) {
      return Number(ligne.qnt) || 0;
    }
    if (!this.isProductLineBarcodeGroupExpanded(index)) {
      return this.sumProductLineQuantities(
        this.getProductLineGroupMemberIndices(this.getProductLineGroupKey(index))
      );
    }
    if (
      this.productLineCouleurGroupCanToggle(index) &&
      !this.isProductLineCouleurGroupExpanded(index) &&
      this.isProductLineCouleurHead(index)
    ) {
      return this.sumProductLineQuantities(this.getProductLineCouleurMemberIndices(index));
    }
    return Number(ligne.qnt) || 0;
  }

  getProductLineRemiseDisplay(index: number): string {
    if (this.isProductLineInCollapsedGroup(index)) {
      return '—';
    }
    const ligne = this.getLineAt(index);
    return ligne ? `${ligne.remise}%` : '—';
  }

  getProductLinePrixUnitaireDisplay(index: number): string {
    if (
      this.isProductLineInCollapsedGroup(index) &&
      !this.isProductLineRefGroupCollapsedHead(index) &&
      !this.isProductLineCouleurGroupCollapsedHead(index)
    ) {
      return '';
    }
    const ligne = this.getLineAt(index);
    if (!ligne) {
      return '0,000';
    }
    if (this.isProductLineRefGroupCollapsedHead(index) || this.isProductLineCouleurGroupCollapsedHead(index)) {
      return '—';
    }
    if (!this.productLineGroupCanToggle(index)) {
      return this.formatPrice(this.getLineAchatUnitPrice(ligne));
    }
    if (this.isProductLineInCollapsedGroup(index)) {
      return '';
    }
    return this.formatPrice(this.getLineAchatUnitPrice(ligne));
  }

  /** Colonne « prix de vente » : vente_ht catalogue (unitaire). */
  getProductLinePrixVenteDisplay(index: number): string {
    if (
      this.isProductLineInCollapsedGroup(index) &&
      !this.isProductLineRefGroupCollapsedHead(index) &&
      !this.isProductLineCouleurGroupCollapsedHead(index)
    ) {
      return '';
    }
    const ligne = this.getLineAt(index);
    if (!ligne) {
      return '0,000';
    }
    if (this.isProductLineRefGroupCollapsedHead(index) || this.isProductLineCouleurGroupCollapsedHead(index)) {
      return '—';
    }
    if (!this.productLineGroupCanToggle(index)) {
      return this.formatPrice(this.getLineVenteUnitPrice(ligne));
    }
    if (this.isProductLineInCollapsedGroup(index)) {
      return '';
    }
    return this.formatPrice(this.getLineVenteUnitPrice(ligne));
  }

  getProductLineTotalDisplay(index: number): string {
    const ligne = this.getLineAt(index);
    if (!ligne) {
      return '0,000';
    }
    if (
      this.isProductLineRefGroupCollapsedHead(index) ||
      this.isProductLineCouleurGroupCollapsedHead(index)
    ) {
      return '—';
    }
    if (this.isProductLineInCollapsedGroup(index)) {
      return '';
    }
    if (this.refGroupCollapsedSecondPriceIsDash) {
      return this.formatPrice(this.getLineVenteUnitPrice(ligne));
    }
    if (!this.productLineGroupCanToggle(index)) {
      return this.formatPrice(this.getLineTotalHT(ligne));
    }
    if (!this.isProductLineBarcodeGroupExpanded(index) && this.isProductLineGroupHead(index)) {
      return this.formatPrice(
        this.sumProductLineHtNet(
          this.getProductLineGroupMemberIndices(this.getProductLineGroupKey(index))
        )
      );
    }
    if (
      this.productLineCouleurGroupCanToggle(index) &&
      !this.isProductLineCouleurGroupExpanded(index) &&
      this.isProductLineCouleurHead(index)
    ) {
      return this.formatPrice(this.sumProductLineHtNet(this.getProductLineCouleurMemberIndices(index)));
    }
    return this.formatPrice(this.getLineTotalHT(ligne));
  }

  toggleProductLineBarcodeGroup(index: number, event?: Event): void {
    event?.preventDefault();
    event?.stopPropagation();
    if (!this.productLineGroupCanToggle(index)) {
      return;
    }
    const key = this.getProductLineGroupKey(index);
    if (this.expandedBarcodeGroups.has(key)) {
      this.expandedBarcodeGroups.delete(key);
      for (const ck of Array.from(this.expandedCouleurGroups)) {
        if (ck.startsWith(`${key}|c:`)) {
          this.expandedCouleurGroups.delete(ck);
        }
      }
    } else {
      this.expandedBarcodeGroups.add(key);
    }
    this.cdr.detectChanges();
    setTimeout(() => feather.replace(), 0);
  }

  toggleProductLineCouleurGroup(index: number, event?: Event): void {
    event?.preventDefault();
    event?.stopPropagation();
    if (!this.productLineCouleurGroupCanToggle(index)) {
      return;
    }
    const key = this.getProductLineCouleurGroupKey(index);
    if (this.expandedCouleurGroups.has(key)) {
      this.expandedCouleurGroups.delete(key);
    } else {
      this.expandedCouleurGroups.add(key);
    }
    this.cdr.detectChanges();
    setTimeout(() => feather.replace(), 0);
  }
}

